import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  List,
  Typography,
  Tag,
  Button,
  Select,
  DatePicker,
  Alert,
  Space,
  Tooltip,
  Badge
} from 'antd';
import {
  DashboardOutlined,
  DollarOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ShopOutlined,
  CalendarOutlined,
  BarChartOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// Types TypeScript
interface DashboardMetrics {
  general_metrics: {
    total_active_companies: number;
    companies_in_trial: number;
    expired_companies: number;
    new_companies_30d: number;
  };
  revenue_metrics: {
    revenue_30d: number;
    revenue_current_month: number;
    avg_transaction_amount: number;
  };
  plan_distribution: Array<{
    plan_name: string;
    plan_price: number;
    currency: string;
    companies_count: number;
    monthly_revenue: number;
  }>;
  expiring_subscriptions: Array<{
    id: number;
    name: string;
    email: string;
    licence_expire_on: string;
    trial_ends_at: string;
    plan_name: string;
    days_until_expiry: number;
    expiry_type: 'trial' | 'subscription';
  }>;
  monthly_evolution: Array<{
    month: string;
    new_subscriptions: number;
  }>;
  recent_transactions: Array<{
    id: number;
    amount: number;
    currency: string;
    transaction_type: string;
    status: string;
    processed_at: string;
    company_name: string;
    plan_name: string;
  }>;
}

const SubscriptionDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('30d');

  const { hasPermission } = useAuth();

  // Charger les métriques du dashboard
  const fetchDashboardMetrics = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/subscription/reports/dashboard', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setMetrics(data.data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Effet pour charger les données au montage
  useEffect(() => {
    if (hasPermission('Admin.Souscription.Reports.view')) {
      fetchDashboardMetrics();
    }
  }, [hasPermission]);

  // Calculer le taux de croissance
  const calculateGrowthRate = () => {
    if (!metrics?.monthly_evolution || metrics.monthly_evolution.length < 2) return 0;
    
    const thisMonth = metrics.monthly_evolution[0]?.new_subscriptions || 0;
    const lastMonth = metrics.monthly_evolution[1]?.new_subscriptions || 0;
    
    if (lastMonth === 0) return thisMonth > 0 ? 100 : 0;
    return ((thisMonth - lastMonth) / lastMonth) * 100;
  };

  // Couleurs pour les graphiques
  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];

  // Données pour le graphique d'évolution
  const evolutionData = metrics?.monthly_evolution?.map(item => ({
    month: dayjs(item.month).format('MMM YYYY'),
    subscriptions: item.new_subscriptions
  })) || [];

  // Données pour le graphique de répartition des plans
  const planDistributionData = metrics?.plan_distribution?.map((plan, index) => ({
    name: plan.plan_name,
    value: plan.companies_count,
    color: COLORS[index % COLORS.length]
  })) || [];

  // Colonnes pour les souscriptions expirant
  const expiringColumns = [
    {
      title: 'Entreprise',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-sm text-gray-500">{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Plan',
      dataIndex: 'plan_name',
      key: 'plan_name',
      render: (planName: string) => (
        <Tag color="blue">{planName}</Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'expiry_type',
      key: 'expiry_type',
      render: (type: string) => (
        <Tag color={type === 'trial' ? 'orange' : 'green'}>
          {type === 'trial' ? 'Essai' : 'Abonnement'}
        </Tag>
      ),
    },
    {
      title: 'Expire dans',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: (days: number) => (
        <span className={days <= 3 ? 'text-red-500 font-bold' : days <= 7 ? 'text-orange-500' : 'text-gray-600'}>
          {days} jour{days > 1 ? 's' : ''}
        </span>
      ),
    },
  ];

  // Vérification des permissions
  if (!hasPermission('Admin.Souscription.Reports.view')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <DashboardOutlined className="text-4xl text-gray-400 mb-4" />
          <Title level={4} type="secondary">Accès non autorisé</Title>
          <Text type="secondary">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </Text>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <DashboardOutlined className="text-4xl text-gray-400 mb-4" />
            <Text type="secondary">Chargement des métriques...</Text>
          </div>
        </div>
      </div>
    );
  }

  const growthRate = calculateGrowthRate();

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <Title level={2}>
            <DashboardOutlined className="mr-2" />
            Tableau de Bord Souscriptions
          </Title>
          <Text type="secondary">
            Vue d'ensemble des performances et métriques des abonnements
          </Text>
        </div>
        
        <Space>
          <Select defaultValue="30d" style={{ width: 120 }}>
            <Option value="7d">7 jours</Option>
            <Option value="30d">30 jours</Option>
            <Option value="90d">90 jours</Option>
          </Select>
          
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchDashboardMetrics}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </div>

      {/* Métriques principales */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Entreprises Actives"
              value={metrics.general_metrics.total_active_companies}
              valueStyle={{ color: '#3f8600' }}
              prefix={<UserOutlined />}
              suffix={
                <Tooltip title="Nombre d'entreprises avec un abonnement actif">
                  <WarningOutlined className="text-gray-400" />
                </Tooltip>
              }
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="En Essai"
              value={metrics.general_metrics.companies_in_trial}
              valueStyle={{ color: '#1890ff' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Expirées"
              value={metrics.general_metrics.expired_companies}
              valueStyle={{ color: '#f5222d' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Nouveaux (30j)"
              value={metrics.general_metrics.new_companies_30d}
              valueStyle={{ color: growthRate >= 0 ? '#3f8600' : '#f5222d' }}
              prefix={growthRate >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              suffix={
                <Text className={`text-sm ${growthRate >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  ({growthRate >= 0 ? '+' : ''}{growthRate.toFixed(1)}%)
                </Text>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Métriques financières */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Revenus 30 jours"
              value={metrics.revenue_metrics.revenue_30d || 0}
              precision={0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<DollarOutlined />}
              suffix="XOF"
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Revenus Mois Actuel"
              value={metrics.revenue_metrics.revenue_current_month || 0}
              precision={0}
              valueStyle={{ color: '#1890ff' }}
              prefix={<DollarOutlined />}
              suffix="XOF"
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Montant Moyen"
              value={metrics.revenue_metrics.avg_transaction_amount || 0}
              precision={0}
              valueStyle={{ color: '#722ed1' }}
              prefix={<BarChartOutlined />}
              suffix="XOF"
            />
          </Card>
        </Col>
      </Row>

      {/* Graphiques et analyses */}
      <Row gutter={[16, 16]} className="mb-6">
        {/* Évolution des souscriptions */}
        <Col xs={24} lg={12}>
          <Card title="Évolution des Nouvelles Souscriptions" className="h-96">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <RechartsTooltip />
                <Area 
                  type="monotone" 
                  dataKey="subscriptions" 
                  stroke="#1890ff" 
                  fill="#1890ff" 
                  fillOpacity={0.3}
                  name="Nouvelles souscriptions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Répartition par plan */}
        <Col xs={24} lg={12}>
          <Card title="Répartition par Plan" className="h-96">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={planDistributionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {planDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Alertes et actions requises */}
      <Row gutter={[16, 16]} className="mb-6">
        {/* Souscriptions expirant bientôt */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <div className="flex items-center">
                <WarningOutlined className="mr-2 text-orange-500" />
                Souscriptions Expirant Bientôt
                <Badge 
                  count={metrics.expiring_subscriptions.length} 
                  className="ml-2"
                  style={{ backgroundColor: '#faad14' }}
                />
              </div>
            }
          >
            {metrics.expiring_subscriptions.length > 0 ? (
              <Table
                columns={expiringColumns}
                dataSource={metrics.expiring_subscriptions}
                pagination={false}
                size="small"
                rowKey="id"
                scroll={{ y: 300 }}
              />
            ) : (
              <div className="text-center py-8">
                <CheckCircleOutlined className="text-4xl text-green-500 mb-2" />
                <div className="text-gray-500">Aucune souscription n'expire dans les 7 prochains jours</div>
              </div>
            )}
          </Card>
        </Col>

        {/* Résumé des plans */}
        <Col xs={24} lg={8}>
          <Card title="Performance des Plans">
            <List
              dataSource={metrics.plan_distribution}
              renderItem={(plan, index) => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex justify-between items-center mb-2">
                      <Text strong>{plan.plan_name}</Text>
                      <Badge count={plan.companies_count} style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    </div>
                    <div className="text-sm text-gray-500 mb-1">
                      Revenus: {plan.monthly_revenue.toLocaleString()} {plan.currency}/mois
                    </div>
                    <Progress 
                      percent={Math.round((plan.companies_count / metrics.general_metrics.total_active_companies) * 100)} 
                      size="small"
                      strokeColor={COLORS[index % COLORS.length]}
                    />
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Transactions récentes */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Transactions Récentes">
            <List
              dataSource={metrics.recent_transactions}
              renderItem={(transaction) => (
                <List.Item>
                  <div className="flex justify-between items-center w-full">
                    <div>
                      <div className="font-medium">{transaction.company_name}</div>
                      <div className="text-sm text-gray-500">
                        {transaction.plan_name} • {transaction.transaction_type}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="font-bold">
                        {transaction.amount.toLocaleString()} {transaction.currency}
                      </div>
                      <div className="text-sm text-gray-500">
                        {dayjs(transaction.processed_at).format('DD/MM HH:mm')}
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SubscriptionDashboard;